/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var LabelButton = class extends HTMLButtonElement {
  constructor() {
    super();

    this.drawer = document.getElementById(this.getAttribute("aria-controls"));
    this.drawerImage = this.drawer.querySelector(
      ".efficiency-label__image-wrapper",
    );
    this.drawerFile = this.drawer.querySelector(".efficiency-label__file-link");
  }

  get imageSource() {
    return this.getAttribute("data-image");
  }

  get fileSource() {
    return this.getAttribute("data-file");
  }

  connectedCallback() {
    this.addEventListener("click", this.handleLabelButton.bind(this));
  }

  handleLabelButton() {
    const image = this.drawerImage.querySelector("img");
    if (this.imageSource && this.imageSource.trim() !== "") {
      this.drawerImage.classList.remove("hidden");
      image.src = this.imageSource;
      image.srcset = this.imageSource;
    } else {
      this.drawerImage.classList.add("hidden");
    }
    if (this.fileSource && this.fileSource.trim() !== "") {
      this.drawerFile.classList.remove("hidden");
      this.drawerFile.href = this.fileSource;
    } else {
      this.drawerFile.classList.add("hidden");
    }
    this.drawer.open = true;
  }
};

window.customElements.define("label-button", LabelButton, {
  extends: "button",
});

document.addEventListener("variant:changed", function (event) {
  const currentVariant = event.detail.variant;
  const productJson = document.querySelector("[data-product-json]")
    ? document.querySelector("[data-product-json]")
    : null;
  const productData = JSON.parse(productJson.innerHTML);
  const variantInData = productData.variants.find((variant) => {
    return variant.id === currentVariant.id;
  });

  if (document.querySelector(".technical-data")) {
    updateTechnicalData(variantInData);
  }

  if (document.querySelector(".product-downloads")) {
    updateDownloads(variantInData);
  }

  if (document.querySelector(".delivery-content")) {
    changeDeliveryContent(variantInData);
  }

  if (document.querySelector(".product-details")) {
    updateProductDetails(variantInData);
  }
});

function updateProductDetails(variantInData) {
  const productDetails = document.querySelector(".product-details");

  productDetails.innerHTML = variantInData.productDetails;
}

function updateDownloads(variantInData) {
  const productDownloads = document.querySelector(".product-downloads");
  const productData = productDownloads.querySelector("#ProductData");
  const energyLabel = productDownloads.querySelector("#EnergyLabel");

  if (productData) {
    productData.href = variantInData.productData.url;
  }

  if (energyLabel) {
    energyLabel.href = variantInData.energyLabel;
  }
}

function changeDeliveryContent(variantInData) {
  const deliveryContent = document.querySelector(".delivery-content");

  deliveryContent.innerHTML = variantInData.deliveryContent;
}

function updateTechnicalData(variantInData) {
  const technicalDataElements = document.querySelectorAll(
    ".technical-data__item",
  );

  technicalDataElements.forEach((element) => {
    element.hidden = true;
    const heading = element.dataset.heading;
    const content = element.querySelector(".technical-data__item-content");

    if (variantInData.metafields[heading]) {
      content.textContent = variantInData.metafields[heading];
      element.hidden = false;
    }
  });
}

/**
 * Updates the cart quantities for specified product variants.
 *
 * @function updateCart
 * @param {Object} updates - An object containing variant IDs and their corresponding quantities.
 * @returns {Promise<void>}
 */
async function updateCart(updates) {
  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
        }),
      );
    })
    .catch((error) => {
      console.error("Failed to update cart:", error);
    });
}

/**
 * Adds items to the cart.
 *
 * @function addItemsToCart
 * @param {Array<number>} variantIds - An array of variant IDs to add to the cart.
 * @returns {Promise<void>}
 */
async function addItemsToCart(variantIds) {
  const items = variantIds.map((id) => ({ id, quantity: 1 }));

  return fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ items }),
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
        }),
      );
    })
    .catch((error) => {
      console.error("Failed to add products to cart:", error);
    });
}

/**
 * Removes specified items from the cart.
 *
 * @function removeItemsFromCart
 * @param {Array<number>} variantIds - An array of variant IDs to remove from the cart.
 * @returns {Promise<void>}
 */
async function removeItemsFromCart(variantIds) {
  const updates = variantIds.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});

  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      setTimeout(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          }),
        );
      }, 1000);
    })
    .catch((error) => {
      console.error("Failed to remove products from cart:", error);
    });
}

/**
 * Clears all items from the cart.
 *
 * @function clearCart
 * @returns {Promise<void>}
 */
async function clearCart() {
  return fetch("/cart/clear.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      setTimeout(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          }),
        );
      }, 500);
    })
    .catch((error) => {
      console.error("Failed to clear the cart:", error);
    });
}

/**
 * Custom element for managing cart goodies based on cart value thresholds.
 *
 * @class CartGoodies
 * @extends HTMLElement
 */
var CartGoodies = class extends HTMLElement {
  connectedCallback() {
    this.goodieForm = this.querySelector('[action="/cart/add"]');
    this.handleCartChange = this.handleCartChange.bind(this);
    this.boundHandleCartChange = (event) => this.handleCartChange(event);

    document.addEventListener("cart:updated", this.boundHandleCartChange);
    if (this.goodieForm) {
      this.goodieForm.addEventListener("submit", this.handleSubmit.bind(this));
    }
  }

  disconnectedCallback() {
    document.removeEventListener("cart:updated", this.boundHandleCartChange);
    if (this.goodieForm) {
      this.goodieForm.removeEventListener(
        "submit",
        this.handleSubmit.bind(this),
      );
    }
  }

  get threshold() {
    return this.dataset.threshold;
  }

  get goodie() {
    return this.dataset.goodie;
  }

  async handleSubmit(event) {
    event.preventDefault();

    const submitButton = this.querySelector("#GoodieAdd");
    submitButton.setAttribute("disabled", "disabled");
    submitButton.setAttribute("aria-busy", "true");

    const productForm = new FormData(event.target);

    const response = await fetch(
      `${window.themeVariables.routes.cartAddUrl}.js`,
      {
        body: productForm,
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      },
    );

    submitButton.removeAttribute("disabled");
    submitButton.removeAttribute("aria-busy");

    const responseJson = await response.json();
    if (response.ok) {
      if (
        window.themeVariables.settings.cartType === "page" ||
        window.themeVariables.settings.pageType === "cart"
      )
        return (window.location.href = `${Shopify.routes.root}cart`);
      this.dispatchEvent(
        new CustomEvent("variant:added", {
          bubbles: !0,
          detail: {
            variant: responseJson.hasOwnProperty("items")
              ? responseJson.items[0]
              : responseJson,
          },
        }),
      ),
        fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
          async (response2) => {
            const cartContent = await response2.json();
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:updated", {
                bubbles: !0,
                detail: { cart: cartContent },
              }),
            ),
              (cartContent.sections = responseJson.sections),
              document.documentElement.dispatchEvent(
                new CustomEvent("cart:refresh", {
                  bubbles: !0,
                  detail: {
                    cart: cartContent,
                    openMiniCart:
                      window.themeVariables.settings.cartType === "drawer" &&
                      this.closest(".drawer") === null,
                  },
                }),
              );
          },
        );
    }
  }

  handleCartChange(event) {
    const cartItems = event.detail.cart.items;
    const cartTotal = event.detail.cart.total_price;

    const allGoodies = cartItems.every((item) => this.goodie === item.id);

    if (allGoodies || cartTotal === 0) {
      clearCart();
      return;
    }

    if (cartTotal < this.threshold) {
      removeItemsFromCart([this.goodie]);
    }
  }
};
window.customElements.define("cart-goodies", CartGoodies);

var PredictiveSearchManual = class extends HTMLElement {
  connectedCallback() {
    this.inputElement = this.querySelector('[name="q"]');
    this.loadingStateElement = this.closest("product-facet").querySelector(
      ".predictive-search__loading-state",
    );
    this.resultsElement = this.closest("product-facet").querySelector(
      ".predictive-search__results",
    );

    // Event delegation for input
    this.addEventListener("input", (event) => {
      const target = event.target.closest('[name="q"]');
      if (target) {
        this._debounce(this._onSearch.bind(this), 200)(event, target);
      }
    });

    // Event delegation for reset button
    this.addEventListener("click", (event) => {
      const target = event.target.closest('[data-action="reset-search"]');
      if (target) {
        this._startNewSearch();
      }
    });
  }

  async _onSearch(event, target) {
    if (event.key !== "Enter") {
      if (this.abortController) this.abortController.abort();
      this.manualProducts =
        this.closest("product-facet").querySelector("#facet-main");

      if (target.value === "") {
        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = true;
        if (this.menuListElement) this.menuListElement.hidden = false;
        this.manualProducts.hidden = false;
      } else {
        this.loadingStateElement.hidden = false;
        this.manualProducts.hidden = true;
        this.resultsElement.hidden = true;
        if (this.menuListElement) this.menuListElement.hidden = true;

        let searchResults = {};
        try {
          this.abortController = new AbortController();
          searchResults = this._supportPredictiveApi()
            ? await this._doPredictiveSearch(target.value)
            : await this._doLiquidSearch(target.value);
        } catch (e) {
          if (e.name === "AbortError") return;
        }

        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = false;
        if (this.menuListElement) this.menuListElement.hidden = true;
        this.resultsElement.innerHTML = searchResults.html;
      }
    }
  }

  async _doPredictiveSearch(term) {
    const response = await fetch(
      `${
        window.themeVariables.routes.predictiveSearchUrl
      }?q=${encodeURIComponent(
        term,
      )}&resources[limit]=10&resources[type]=product&resources[options][fields]=title,product_type,variants.title,variants.sku&section_id=predictive-search-manuals`,
      { signal: this.abortController.signal },
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    return {
      hasResults:
        div.querySelector(".predictive-search__results-categories") !== null,
      html: div.firstElementChild.innerHTML,
    };
  }

  async _doLiquidSearch(term) {
    const response = await fetch(
      `${window.themeVariables.routes.searchUrl}?q=${encodeURIComponent(
        term,
      )}&resources[limit]=50&section_id=predictive-search-compatibility`,
      { signal: this.abortController.signal },
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    return {
      hasResults:
        div.querySelector(".predictive-search__results-categories") !== null,
      html: div.firstElementChild.innerHTML,
    };
  }

  _startNewSearch() {
    this.inputElement.value = "";
    this.inputElement.focus();
    const event = new Event("input", { bubbles: true, cancelable: true });
    this.inputElement.dispatchEvent(event);
  }

  _supportPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)
      .predictiveSearch;
  }

  _debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
};

window.customElements.define(
  "predictive-search-manual",
  PredictiveSearchManual,
);

var PredictiveSearchWarranty = class extends HTMLElement {
  connectedCallback() {
    this.inputElement = this.querySelector('[name="q"]');
    this.loadingStateElement = this.closest("product-facet").querySelector(
      ".predictive-search__loading-state",
    );
    this.resultsElement = this.closest("product-facet").querySelector(
      ".predictive-search__results",
    );

    this.addEventListener("input", (event) => {
      const target = event.target.closest('[name="q"]');
      if (target) {
        this._debounce(this._onSearch.bind(this), 200)(event, target);
      }
    });

    this.addEventListener("click", (event) => {
      const target = event.target.closest('[data-action="reset-search"]');
      if (target) {
        this._startNewSearch();
      }
    });
  }

  async _onSearch(event, target) {
    if (event.key !== "Enter") {
      if (this.abortController) this.abortController.abort();
      this.warrantyProducts =
        this.closest("product-facet").querySelector("#facet-main");

      if (target.value === "") {
        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = true;
        if (this.menuListElement) this.menuListElement.hidden = false;
        this.warrantyProducts.hidden = false;
      } else {
        this.loadingStateElement.hidden = false;
        this.warrantyProducts.hidden = true;
        this.resultsElement.hidden = true;
        if (this.menuListElement) this.menuListElement.hidden = true;

        let searchResults = {};
        try {
          this.abortController = new AbortController();
          searchResults = this._supportPredictiveApi()
            ? await this._doPredictiveSearch(target.value)
            : await this._doLiquidSearch(target.value);
        } catch (e) {
          if (e.name === "AbortError") return;
        }

        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = false;
        if (this.menuListElement) this.menuListElement.hidden = true;
        this.resultsElement.innerHTML = searchResults.html;
      }
    }
  }

  async _doPredictiveSearch(term) {
    const response = await fetch(
      `${
        window.themeVariables.routes.predictiveSearchUrl
      }?q=${encodeURIComponent(
        term,
      )}&resources[limit]=10&resources[type]=product&resources[options][fields]=title,product_type,variants.title,variants.sku&section_id=predictive-search-warranties`,
      { signal: this.abortController.signal },
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    return {
      hasResults:
        div.querySelector(".predictive-search__results-categories") !== null,
      html: div.firstElementChild.innerHTML,
    };
  }

  async _doLiquidSearch(term) {
    const response = await fetch(
      `${window.themeVariables.routes.searchUrl}?q=${encodeURIComponent(
        term,
      )}&resources[limit]=50&section_id=predictive-search-compatibility`,
      { signal: this.abortController.signal },
    );
    const div = document.createElement("div");
    div.innerHTML = await response.text();
    return {
      hasResults:
        div.querySelector(".predictive-search__results-categories") !== null,
      html: div.firstElementChild.innerHTML,
    };
  }

  _startNewSearch() {
    this.inputElement.value = "";
    this.inputElement.focus();
    const event = new Event("input", { bubbles: true, cancelable: true });
    this.inputElement.dispatchEvent(event);
  }

  _supportPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)
      .predictiveSearch;
  }

  _debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
};

window.customElements.define(
  "predictive-search-warranty",
  PredictiveSearchWarranty,
);

var FeaturedMediaVideo = class extends HTMLElement {
  async connectedCallback() {
    this.videoModal = document.querySelector(".product__media-video-popup");
    this.videoContainer = document.querySelector(
      ".product__media-video-popup-container",
    );
    this.closeButton = document.querySelector(
      ".product__media-video-popup-close-wrapper",
    );
    this.videoSource = this.dataset.videoSource;

    this.init();
  }

  async init() {
    this.addEventListener("click", () => {
      this.videoModal.classList.add("modal--active");
      this.videoContainer.innerHTML = `<video src="${this.videoSource}" autoplay controls></video>`;
    });

    this.closeButton.addEventListener("click", () => {
      this.videoModal.classList.remove("modal--active");
      this.videoContainer.innerHTML = "";
    });
  }
};
window.customElements.define("featured-media-video", FeaturedMediaVideo);

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loadMoreButton = this.querySelector("[data-load-more]");
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener("click", this.loadMore.bind(this));
    }
    this.sectionId = this.getAttribute("data-section-id");
  }

  get apparitionAnimation() {
    return new CustomAnimation(
      new ParallelEffect(
        this.productItems.map((item, index) => {
          return new CustomKeyframeEffect(
            item,
            {
              opacity: [0, 1],
              transform: [
                `translateY(${
                  MediaFeatures.prefersReducedMotion()
                    ? 0
                    : window.innerWidth < 1e3
                      ? 35
                      : 60
                }px)`,
                "translateY(0)",
              ],
            },
            {
              duration: 600,
              delay: MediaFeatures.prefersReducedMotion()
                ? 0
                : 100 * index - Math.min(3 * index * index, 100 * index),
              easing: "ease",
            },
          );
        }),
      ),
    );
  }

  // async _rerender() {
  //   const nextUrl = new URL(window.location.href);
  //   nextUrl.searchParams.set("section_id", this.sectionId);

  //   try {
  //     const response = await fetch(nextUrl);
  //     const responseText = await response.text();
  //     const tempDiv = document.createElement("div");
  //     tempDiv.innerHTML = responseText;
  //     this.productGrid = document.getElementById("product-grid");
  //     this.productGrid.innerHTML = tempDiv.querySelector("#product-grid").innerHTML;;

  //     this.productList = this.querySelector("product-list");

  //     this.productItems = Array.from(
  //       this.productList.querySelectorAll("product-item")
  //     );
  //     this.productItems.forEach((item) => (item.style.opacity = 0));
  //     await this.productList.untilVisible({
  //       threshold:
  //         this.productList.clientHeight > 0
  //           ? Math.min(50 / this.productList.clientHeight, 1)
  //           : 0,
  //     });
  //     this.apparitionAnimation.play();
  //   } catch (error) {
  //     console.error("Error loading more products:", error);
  //   }
  // }

  async loadMore() {
    this.loadMoreButton.setAttribute("disabled", "disabled");
    this.loadMoreButton.setAttribute("aria-busy", "true");
    const nextUrl = new URL(window.location.href);
    const currentPage = parseInt(nextUrl.searchParams.get("page") || 1);
    nextUrl.searchParams.set("page", currentPage + 1);
    nextUrl.searchParams.set("section_id", this.sectionId);

    try {
      this.loadMoreButton.classList.add("button--loading");
      const response = await fetch(nextUrl);
      const responseText = await response.text();

      // Create a temporary container
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = responseText;

      // Find the product grid in the response
      const newProducts = tempDiv.querySelector("#product-grid").innerHTML;
      const hasNextPage = tempDiv.querySelector(".load-more-button") !== null;

      // Append new products
      this.productGrid = document.getElementById("product-grid");
      this.productGrid.insertAdjacentHTML("beforeend", newProducts);

      this.productList = this.querySelector("product-list");

      this.productItems = Array.from(
        this.productList.querySelectorAll("product-item"),
      );
      this.productItems.forEach((item) => (item.style.opacity = 0));
      await this.productList.untilVisible({
        threshold:
          this.productList.clientHeight > 0
            ? Math.min(50 / this.productList.clientHeight, 1)
            : 0,
      });
      this.apparitionAnimation.play();

      // Remove button if no more pages
      if (!hasNextPage) {
        this.loadMoreButton.remove();
      }
      nextUrl.searchParams.delete("section_id");
      // Update URL
      window.history.replaceState({}, "", nextUrl.href);
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      this.loadMoreButton.removeAttribute("disabled");
      this.loadMoreButton.removeAttribute("aria-busy");
    }
  }
}

customElements.define("infinite-scroll", InfiniteScroll);

class AddOn extends HTMLElement {
  constructor() {
    super();
  }

  get addOnQuantity() {
    return Number(this.addOnQuantityInput?.getAttribute("data-add-on-quantity")) || 1;
  }

  get mainQuantity() {
    return Number(this.quantitySelector?.value) || 1;
  }

  // Unit add-on metafield × chosen product quantity (quantity breaks / dropdown).
  get totalAddOnQuantity() {
    return this.mainQuantity * this.addOnQuantity;
  }

  get quantityPrice() {
    return this.productForm?.querySelector(
      "quantity-breaks, product-quantity-price"
    );
  }

  get quantityBreaksData() {
    if (this._quantityBreaksData !== undefined) return this._quantityBreaksData;

    try {
      this._quantityBreaksData = JSON.parse(
        this.dataset.quantityBreaks || "null"
      );
    } catch {
      this._quantityBreaksData = null;
    }

    return this._quantityBreaksData;
  }

  // Highest add-on break whose quantity is still <= the add-on line quantity.
  get selectedReduction() {
    const data = this.quantityBreaksData;
    const quantity = this.totalAddOnQuantity;
    if (!data || !(quantity > 0)) return 0;

    let matchingReduction = 0;
    let matchingBreakQuantity = -1;

    for (let i = 1; i <= 10; i++) {
      const breakQuantity = Number(data[`quantity_${i}`]) || 0;
      const reduction = Number(data[`reduction_${i}`]) || 0;

      if (
        breakQuantity > 1 &&
        breakQuantity <= quantity &&
        breakQuantity > matchingBreakQuantity
      ) {
        matchingBreakQuantity = breakQuantity;
        matchingReduction = reduction;
      }
    }

    return matchingReduction;
  }

  get syncPriceWithQuantityBreaks() {
    return (
      this.hasAttribute("data-sync-price-with-quantity-breaks") &&
      this.quantityBreaksData != null &&
      this.quantityPrice?.hasQuantityBreaks === true
    );
  }

  connectedCallback() {
    this.addOnQuantityInput = this.querySelector("[data-add-on-quantity]");
    this.addOnCheckbox = this.querySelector("[data-add-on-id]");
    this.quantityDisplay = this.querySelector("[data-add-on-quantity-display]");
    this.unitPriceDisplay = this.querySelector("[data-add-on-unit-price]");
    this.comparePriceDisplay = this.querySelector("[data-add-on-compare-price]");
    this.comparePriceWrapper = this.querySelector(
      "[data-add-on-compare-price-wrapper]"
    );
    this.salePriceEl = this.querySelector("[data-add-on-sale-price]");
    this.productForm = this.closest(".product-form");
    this.quantitySelector = this.productForm?.querySelector('[name="quantity"]');
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);

    // Keep the Liquid-rendered money string so we can restore it later.
    this.originalUnitPriceText = this.unitPriceDisplay?.textContent ?? "";
    this.originalComparePriceText = this.comparePriceDisplay?.textContent ?? "";
    this.originalHasCompare = Boolean(
      this.comparePriceWrapper && !this.comparePriceWrapper.hidden
    );
    this.originalSaleHighlight = Boolean(
      this.salePriceEl?.classList.contains("price--highlight")
    );

    if (!this.addOnCheckbox) return;

    // Initial visible state comes from Liquid — only wire listeners + hidden input.
    this.syncHiddenQuantityInput();
    this.quantitySelector?.addEventListener("change", this.handleQuantityChange);
    this.addOnCheckbox.addEventListener("change", this.handleCheckboxChange);
    this.updatePriceDisplay();
  }

  disconnectedCallback() {
    this.quantitySelector?.removeEventListener("change", this.handleQuantityChange);
    this.addOnCheckbox?.removeEventListener("change", this.handleCheckboxChange);
  }

  handleQuantityChange() {
    this.updateQuantityInputState();
  }

  handleCheckboxChange() {
    this.updateQuantityInputState();
  }

  formatMoney(cents) {
    const format = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;

    return formatMoney(cents, format);
  }

  get reductionRounding() {
    return this.quantityPrice?.reductionRounding || "line";
  }

  // Line: discount unit price, round sale down to 10 cents, × qty.
  // Total: discount line total, round sale total down to 10 cents.
  getTotalSaving(unitPrice, quantity, reductionPercent) {
    if (!reductionPercent || !unitPrice || !quantity) return 0;

    const fullTotal = unitPrice * quantity;

    if (this.reductionRounding === "total") {
      const saleTotal =
        Math.floor((fullTotal * (100 - reductionPercent)) / 100 / 10) * 10;
      return fullTotal - saleTotal;
    }

    const unitSale =
      Math.floor((unitPrice * (100 - reductionPercent)) / 100 / 10) * 10;
    return (unitPrice - unitSale) * quantity;
  }

  updateQuantityDisplay() {
    if (!this.quantityDisplay) return;

    const nextQuantity = String(this.totalAddOnQuantity);
    if (this.quantityDisplay.textContent !== nextQuantity) {
      this.quantityDisplay.textContent = nextQuantity;
    }
  }

  // Show unit sale price from the add-on's own quantity_breaks for its line qty.
  updatePriceDisplay() {
    if (!this.syncPriceWithQuantityBreaks || !this.unitPriceDisplay) return;

    const unitPrice = Number(this.dataset.price) || 0;
    const nativeCompareAt = Number(this.dataset.compareAtPrice) || 0;
    const quantity = this.totalAddOnQuantity;
    const totalSaving = this.getTotalSaving(
      unitPrice,
      quantity,
      this.selectedReduction
    );

    // No break discount: restore original Liquid markup/text (avoids formatMoney flicker).
    if (totalSaving <= 0) {
      if (this.unitPriceDisplay.textContent !== this.originalUnitPriceText) {
        this.unitPriceDisplay.textContent = this.originalUnitPriceText;
      }
      this.salePriceEl?.classList.toggle(
        "price--highlight",
        this.originalSaleHighlight
      );
      if (this.comparePriceWrapper) {
        this.comparePriceWrapper.hidden = !this.originalHasCompare;
      }
      if (
        this.comparePriceDisplay &&
        this.comparePriceDisplay.textContent !== this.originalComparePriceText
      ) {
        this.comparePriceDisplay.textContent = this.originalComparePriceText;
      }
      return;
    }

    const salePrice = Math.max(
      Math.floor((unitPrice * quantity - totalSaving) / quantity),
      0
    );
    let compareAt = nativeCompareAt;

    if (!(compareAt > salePrice)) {
      compareAt = unitPrice;
    }

    const formattedSalePrice = this.formatMoney(salePrice);
    if (this.unitPriceDisplay.textContent !== formattedSalePrice) {
      this.unitPriceDisplay.textContent = formattedSalePrice;
    }

    this.salePriceEl?.classList.add("price--highlight");
    if (this.comparePriceWrapper) {
      this.comparePriceWrapper.hidden = false;
    }
    if (this.comparePriceDisplay) {
      const formattedCompareAt = this.formatMoney(compareAt);
      if (this.comparePriceDisplay.textContent !== formattedCompareAt) {
        this.comparePriceDisplay.textContent = formattedCompareAt;
      }
    }
  }

  syncHiddenQuantityInput() {
    if (!this.addOnCheckbox || !this.addOnQuantityInput) return;

    const propertyInputs = this.querySelectorAll("[data-add-on-property]");

    if (this.addOnCheckbox.checked) {
      this.addOnQuantityInput.disabled = false;
      this.addOnQuantityInput.value = this.totalAddOnQuantity;
      propertyInputs.forEach((input) => {
        input.disabled = false;
      });
    } else {
      this.addOnQuantityInput.disabled = true;
      this.addOnQuantityInput.value = 0;
      propertyInputs.forEach((input) => {
        input.disabled = true;
      });
    }
  }

  updateQuantityInputState() {
    this.updateQuantityDisplay();
    this.updatePriceDisplay();
    this.syncHiddenQuantityInput();
  }
}

customElements.define("add-on", AddOn);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
  }

  connectedCallback() {
    this.sliderConfig = JSON.parse(this.dataset.sliderConfig);
    this.slider = new Swiper(this, this.sliderConfig);
  }

  disconnectedCallback() {
    this.slider.destroy(true, true);
  }
}

customElements.define("slider-component", SliderComponent);

class ComparisonGraph extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
  }

  connectedCallback() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) {
      // Fallback: animate immediately if no support
      this.animateGraph();
      return;
    }

    const options = {
      root: null,
      rootMargin: "0px 0px -20% 0px", // Trigger when element is 20% visible
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.animateGraph();
          this.hasAnimated = true;
          // Stop observing after animation is triggered
          this.observer.unobserve(this);
        }
      });
    }, options);

    this.observer.observe(this);
  }

  animateGraph() {
    // Add the animate class to trigger CSS animation
    this.classList.add("comparison-graph--animate");

    // Also trigger individual item animations with staggered delay
    const items = this.querySelectorAll(".comparison-graph__item");
    items.forEach((item, index) => {
      // Store the target width from the inline style
      const targetWidth = item.style.width;
      item.style.setProperty("--target-width", targetWidth);

      item.classList.add("comparison-graph__item--animate");
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

customElements.define("comparison-graph", ComparisonGraph);

class DiscountInput extends HTMLElement {
  constructor() {
    super();
  }

  get translations() {
    return window.translations.discount;
  }

  connectedCallback() {
    this.discountForm = this.querySelector("form");
    this.discountInput = this.querySelector("input[name='discount']");
    this.discountErrorMessage = this.querySelector(
      ".discount-input__error-message",
    );
    this.discountApplications = this.dataset.discountApplications.split(",");
    this.discountRemoveButtons = this.querySelectorAll("[data-discount]");

    this.discountForm.addEventListener("submit", this.handleSubmit.bind(this));
    this.discountRemoveButtons.forEach((button) => {
      button.addEventListener("click", this.handleRemoveDiscount.bind(this));
    });
  }

  handleRemoveDiscount(event) {
    const discount = event.currentTarget.dataset.discount;
    const applicationNames = this.discountApplications
      .filter((app) => app !== discount)
      .join(",");

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ discount: applicationNames }),
    })
      .then((response) => {
        return response.json();
      })
      .then(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          }),
        );
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  handleSubmit(event) {
    event.preventDefault();
    const discount = this.discountInput.value;

    let applicationNames;
    if (
      this.discountApplications &&
      this.discountApplications.length > 0 &&
      this.discountApplications[0] !== ""
    ) {
      applicationNames = [...this.discountApplications, discount].join(",");
    } else {
      applicationNames = discount;
    }

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ discount: applicationNames }),
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        const wrongDiscountCodes =
          data.discount_codes.find((code) => !code.applicable) || null;

        const alreadyApplied =
          data.discount_codes.find(
            (code) =>
              code.code &&
              code.code.toLowerCase() === discount.toLowerCase() &&
              code.applicable &&
              this.discountApplications
                .map((c) => c.trim().toLowerCase())
                .includes(code.code.toLowerCase()),
          ) || null;

        if (wrongDiscountCodes || alreadyApplied) {
          this.discountInput.value = "";
          this.discountInput.classList.remove("is-filled");
          const message = wrongDiscountCodes
            ? `${this.translations.invalid.replace("{{ code }}", wrongDiscountCodes.code)}`
            : `${this.translations.already_applied.replace("{{ code }}", alreadyApplied.code)}`;

          this.discountErrorMessage.classList.remove("hidden");
          this.discountErrorMessage.textContent = message;

          setTimeout(() => {
            this.discountErrorMessage.classList.add("hidden");
            this.discountErrorMessage.innerHTML = "";
          }, 3000);
        } else {
          this.discountErrorMessage.classList.add("hidden");
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", {
              bubbles: true,
            }),
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }
}

customElements.define("discount-input", DiscountInput);

class VideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    this.toggle = this.querySelector(".video-player__toggle");
    this.playIcon = this.querySelector(".video-player__icon--play");
    this.pauseIcon = this.querySelector(".video-player__icon--pause");

    if (!this.video || !this.toggle) return;

    this.toggle.addEventListener("click", () => this._handleToggle());

    this.video.addEventListener("ended", () => this._showPlay());
  }

  _handleToggle() {
    if (this.video.paused) {
      this.video.play();
      this._showPause();
    } else {
      this.video.pause();
      this._showPlay();
    }
  }

  _showPause() {
    this.classList.add("is-playing");
    this.playIcon.classList.add("hidden");
    this.pauseIcon.classList.remove("hidden");
    this.toggle.setAttribute("aria-label", "Pause video");
  }

  _showPlay() {
    this.classList.remove("is-playing");
    this.pauseIcon.classList.add("hidden");
    this.playIcon.classList.remove("hidden");
    this.toggle.setAttribute("aria-label", "Play video");
  }
}

customElements.define("video-player", VideoPlayer);

class MenuPanelTrigger extends HTMLButtonElement {
  connectedCallback() {
    this.boundHandleClick = this._handleClick.bind(this);
    this.addEventListener('click', this.boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundHandleClick);
  }

  _handleClick() {
    const panelId = this.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);
    if (panel) panel.open();
  }
}

customElements.define('menu-panel-trigger', MenuPanelTrigger, { extends: 'button' });

class MobileMenuPanel extends HTMLElement {
  connectedCallback() {
    const nav = this.closest('mobile-navigation');
    const drawerContent = nav && nav.querySelector(':scope > .drawer__content');
    const target = drawerContent || nav;

    if (target && this.parentElement !== target) {
      target.appendChild(this);
      return;
    }

    if (this._initialized) return;
    this._initialized = true;

    this._backButton = this.querySelector(':scope > .mobile-menu-panel__header [data-action="back"]');
    if (this._backButton) {
      this.boundHandleBack = this._handleBack.bind(this);
      this._backButton.addEventListener('click', this.boundHandleBack);
    }
  }

  disconnectedCallback() {
    if (this._backButton && this.boundHandleBack) {
      this._backButton.removeEventListener('click', this.boundHandleBack);
    }
    this._initialized = false;
  }

  open() {
    this.setAttribute('open', '');
    this.scrollTop = 0;
  }

  close() {
    this.removeAttribute('open');
  }

  _handleBack() {
    this.close();
  }
}

customElements.define('mobile-menu-panel', MobileMenuPanel);

var OpenTooltip = class extends HTMLElement {
  static get observedAttributes() {
    return ["open"];
  }

  constructor() {
    super();
    this._onOutsideClick = this._onOutsideClick.bind(this);
    this._onEscapeKey = this._onEscapeKey.bind(this);
    this._onCloseAction = this._onCloseAction.bind(this);
  }

  connectedCallback() {
    // Set initial hidden state if not open
    if (!this.hasAttribute("open")) {
      this.classList.add("hidden");
    }

    // Listen for close action clicks inside the tooltip
    this.addEventListener("click", this._onCloseAction);
  }

  disconnectedCallback() {
    this._removeEventListeners();
    this.removeEventListener("click", this._onCloseAction);
  }

  get open() {
    return this.hasAttribute("open");
  }

  set open(value) {
    if (value) {
      // Close all other tooltips before opening this one
      this._closeAllOtherTooltips();
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      if (oldValue === null && newValue === "") {
        // Tooltip is opening
        this._onOpen();
      } else if (newValue === null) {
        // Tooltip is closing
        this._onClose();
      }
    }
  }

  _onOpen() {
    this.classList.remove("hidden");

    // Dispatch event for toggle-button to sync its aria-expanded state
    this.dispatchEvent(
      new CustomEvent("openable-element:open", { bubbles: true })
    );

    // Add event listeners for closing
    setTimeout(() => {
      document.addEventListener("click", this._onOutsideClick);
    }, 0);
    document.addEventListener("keydown", this._onEscapeKey);
  }

  _onClose() {
    this.classList.add("hidden");

    // Dispatch event for toggle-button to sync its aria-expanded state
    this.dispatchEvent(
      new CustomEvent("openable-element:close", { bubbles: true })
    );

    // Remove event listeners
    this._removeEventListeners();
  }

  _removeEventListeners() {
    document.removeEventListener("click", this._onOutsideClick);
    document.removeEventListener("keydown", this._onEscapeKey);
  }

  _onOutsideClick(event) {
    // Check if the click is outside this tooltip and its toggle button
    const toggleButton = document.querySelector(`[aria-controls="${this.id}"]`);

    if (
      !this.contains(event.target) &&
      (!toggleButton || !toggleButton.contains(event.target))
    ) {
      this.open = false;
    }
  }

  _onEscapeKey(event) {
    if (event.key === "Escape") {
      this.open = false;
    }
  }

  _onCloseAction(event) {
    if (event.target.closest('[data-action="close-tooltip"]')) {
      this.open = false;
    }
  }

  _closeAllOtherTooltips() {
    document.querySelectorAll("open-tooltip[open]").forEach((tooltip) => {
      if (tooltip !== this) {
        tooltip.open = false;
      }
    });
  }
};

window.customElements.define("open-tooltip", OpenTooltip);

class BundlePicker extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  get currentColor() {
    return this.getAttribute("data-current-color");
  }

  connectedCallback() {
    this.bundleVariantData = JSON.parse(
      document.querySelector("[data-bundle-variant-data]").textContent
    );
    this.initSelectors();
  }

  initSelectors() {
    this.selectors = this.querySelectorAll("input.color-swatch__radio");
    this.selectors.forEach((selector) => {
      selector.addEventListener("change", this.handleSelectorChange.bind(this));
    });
  }

  handleSelectorChange(event) {
    this.setAttribute("data-current-color", event.currentTarget.dataset.color);
    const variantId = event.currentTarget.value;
    const url =
      window.location.pathname +
      "?section_id=" +
      this.sectionId +
      "&variant=" +
      variantId;
    fetch(url)
      .then((response) => response.text())
      .then((data) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = data;

        this.updateSelector(tempDiv);

        this.bundleVariantData.bundle_products.forEach((product) => {
          const currentColorVariant = product.variants.find((variant) => {
            return variant.color === this.currentColor;
          });
          if (currentColorVariant) {
            this.updateDrawerImages(product, currentColorVariant);
            this.updateDrawerPoolImages(product, currentColorVariant);
          }
        });

        this.dispatchEvent(
          new CustomEvent("bundle-picker:color-changed", {
            bubbles: true,
            detail: { newColor: this.currentColor },
          })
        );

        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.delete("section_id");
        window.history.replaceState({}, "", urlObj.pathname + urlObj.search);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  updateSelector(tempDiv) {
    const selector = document.querySelector(".bundle-picker__color-selector");
    const newSelector = tempDiv.querySelector(".bundle-picker__color-selector");
    if (selector) {
      selector.innerHTML = newSelector.innerHTML;
    }
    this.initSelectors();
  }

  updateDrawerImages(product, currentColorVariant) {
    const productItemImages = Array.from(
      this.querySelectorAll(".product-item__aspect-ratio")
    );
    const currentItemImage = productItemImages.find((image) => {
      return parseInt(image.dataset.productId) === parseInt(product.id);
    });

    if (currentItemImage) {
      const tempImgWrapper = document.createElement("div");
      tempImgWrapper.innerHTML = currentColorVariant.image;
      const newImg = tempImgWrapper.querySelector("img");
      const oldImg = currentItemImage.querySelector("img");
      if (newImg && oldImg) {
        oldImg.replaceWith(newImg);
      }

      const currentQuantityInput = currentItemImage
        .closest(".product-item")
        .querySelector('input[name="quantity"]');
      if (currentQuantityInput) {
        currentQuantityInput.setAttribute(
          "data-variant-id",
          currentColorVariant.id
        );
        currentQuantityInput.setAttribute(
          "data-image",
          currentColorVariant.image
        );
        currentQuantityInput.setAttribute("data-color", this.currentColor);
        if (currentColorVariant.price != null) {
          currentQuantityInput.setAttribute(
            "data-price",
            currentColorVariant.price
          );
        }
      }
    }
  }

  updateDrawerPoolImages(product, currentColorVariant) {
    const productPoolItems = Array.from(
      this.querySelectorAll(".bundle-picker__product-pool-item")
    );
    const currentProductPoolItems = productPoolItems.filter((item) => {
      return parseInt(item.dataset.productId) === parseInt(product.id);
    });

    if (currentProductPoolItems.length > 0) {
      currentProductPoolItems.forEach((item) => {
        const tempImgWrapper = document.createElement("div");
        tempImgWrapper.innerHTML = currentColorVariant.image;
        const newImg = tempImgWrapper.querySelector("img");
        const oldImg = item.querySelector("img");
        if (newImg && oldImg) {
          oldImg.replaceWith(newImg);
        }
        item.setAttribute("data-variant-id", currentColorVariant.id);
      });
    }
  }
}

customElements.define("bundle-picker", BundlePicker);

var BundleQuantitySelector = class extends CustomHTMLElement {
  connectedCallback() {
    (this.inputElement = this.querySelector("input")),
      this.delegate.on("click", "button:first-child", this.stepDown.bind(this)),
      this.delegate.on("click", "button:last-child", this.stepUp.bind(this));
  }
  stepUp() {
    this.inputElement.stepUp(),
      this.inputElement.dispatchEvent(new Event("change", { bubbles: !0 }));
  }
  stepDown() {
    this.inputElement.stepDown(),
      this.inputElement.dispatchEvent(new Event("change", { bubbles: !0 }));
  }
};
window.customElements.define(
  "bundle-quantity-selector",
  BundleQuantitySelector
);
var BundleInputNumber = class extends HTMLInputElement {
  connectedCallback() {
    this.addEventListener("input", this._onValueInput.bind(this)),
      this.addEventListener("change", this._onValueChanged.bind(this));
  }
  get quantity() {
    return parseInt(this.value);
  }
  set quantity(quantity) {
    const isNumeric =
      (typeof quantity == "number" ||
        (typeof quantity == "string" && quantity.trim() !== "")) &&
      !isNaN(quantity);
    quantity !== "" &&
      ((!isNumeric || quantity < 0) && (quantity = parseInt(quantity) || 1),
      (this.value = Math.max(
        this.min || 1,
        Math.min(quantity, this.max || Number.MAX_VALUE)
      ).toString()),
      this.checkValidity() || this.stepDown());
  }
  _onValueInput() {
    (this.quantity = this.value),
      this.style.setProperty(
        "--quantity-selector-character-count",
        `${this.value.length}ch`
      );
  }
  _onValueChanged() {
    this.value === "" && (this.quantity = this.min || 1),
      this.checkValidity() || this.stepDown(),
      this.style.setProperty(
        "--quantity-selector-character-count",
        `${this.value.length}ch`
      );
  }
};
window.customElements.define("bundle-input-number", BundleInputNumber, {
  extends: "input",
});

class BundleDrawer extends HTMLElement {
  constructor() {
    super();
  }

  get drawerId() {
    return this.closest(".bundle-drawer").id;
  }

  connectedCallback() {
    this.addButton = this.closest(".bundle-drawer").querySelector(
      ".bundle-picker__drawer-add-to-cart"
    );
    this.addPrice = this.addButton.querySelector(
      ".bundle-picker__product-pool-price"
    );
    this.addText = this.addButton.querySelector(
      ".bundle-picker__product-pool-price-text"
    );
    this.bundlePickerTile = this.closest(".bundle-picker").querySelector(
      `[aria-controls="${this.drawerId}"]`
    );
    this.bundlePickerPool = this.bundlePickerTile.querySelector(
      ".bundle-picker__step-pool"
    );
    this.bundlePickerSteps = this.bundlePickerTile.querySelector(
      ".bundle-picker__item-action-steps"
    );
    this.bundlePickerLink = this.bundlePickerTile.querySelector(
      ".bundle-picker__item-action-link"
    );
    this.pickerPool = this.closest(".bundle-drawer").querySelector(
      ".bundle-picker__product-pool"
    );
    this.inputs = this.querySelectorAll("input[name='quantity']");
    this.productPool = [];
    this.inputs.forEach((input) => {
      input.addEventListener("change", this.handleInputChange.bind(this));
    });

    this.addButton.addEventListener(
      "click",
      this.handleAddButtonClick.bind(this)
    );

    this.closest(".bundle-picker").addEventListener(
      "bundle-picker:color-changed",
      this.handleColorChange.bind(this)
    );
  }

  handleColorChange() {
    this.productPool = this.productPool.map((item) => {
      const input = Array.from(this.inputs).find(
        (inp) => inp.dataset.productId === item.productId
      );
      if (!input) return item;
      return {
        ...item,
        variantId: input.dataset.variantId,
        image: input.dataset.image,
        price: parseFloat(input.dataset.price) || 0,
        color: input.dataset.color,
      };
    });
    this.renderPool();
    this.checkAddButton();
    if (this.bundlePickerTile?.getAttribute("data-selected") === "true") {
      this.bundlePickerPool.innerHTML = this.pickerPool.innerHTML;
      this.bundlePickerTile.setAttribute(
        "data-price",
        this.productPool.reduce(
          (acc, product) => acc + product.price * product.quantity,
          0
        )
      );
    }
  }

  handleInputChange(event) {
    const input = event.currentTarget;
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value, 10) || 0;

    if (quantity === 0) {
      this.productPool = this.productPool.filter(
        (p) => p.variantId !== variantId
      );
    } else {
      const item = {
        variantId,
        productId: input.dataset.productId,
        image: input.dataset.image,
        title: input.dataset.title,
        quantity,
        price: parseFloat(input.dataset.price) || 0,
        color: input.dataset.color,
      };
      const existingIndex = this.productPool.findIndex(
        (p) => p.variantId === variantId
      );
      if (existingIndex >= 0) {
        this.productPool[existingIndex] = item;
      } else {
        this.productPool.push(item);
      }
    }

    this.renderPool();
    this.checkAddButton();
  }

  renderPool() {
    const items = this.productPool
      .map(
        ({ variantId, productId, image, title, quantity }) => `
      <div class="bundle-picker__product-pool-item" data-product-id="${productId}" data-variant-id="${variantId}" data-quantity="${quantity}">
        <div class="bundle-picker__product-pool-item-image">
          ${image}
          <div class="bundle-picker__product-pool-item-quantity-wrapper">
            <span class="bundle-picker__product-pool-quantity">${quantity}</span>
          </div>
        </div>
      </div>
    `
      )
      .join("");

    this.pickerPool.innerHTML = items;
  }

  checkAddButton() {
    if (this.productPool.length > 0) {
      this.addButton.disabled = false;
      this.addButton.classList.remove("button--ternary");
      this.addButton.classList.add("button--primary");
      this.addPrice.textContent = formatMoney(
        this.productPool.reduce(
          (acc, product) => acc + product.price * product.quantity,
          0
        )
      );
      this.addText.textContent = "Hinzufügen";
    } else {
      this.addButton.disabled = true;
      this.addButton.classList.remove("button--primary");
      this.addButton.classList.add("button--ternary");
      this.addPrice.textContent = "";
      this.addText.textContent = "Wähle Produkte";
    }
  }

  handleAddButtonClick(event) {
    this.bundlePickerPool.classList.remove("hidden");
    this.bundlePickerSteps.classList.add("hidden");
    this.bundlePickerLink.textContent = "Bearbeiten";
    this.bundlePickerTile.setAttribute("data-selected", "true");
    this.bundlePickerTile.setAttribute(
      "data-price",
      this.productPool.reduce(
        (acc, product) => acc + product.price * product.quantity,
        0
      )
    );
    this.bundlePickerPool.innerHTML = this.pickerPool.innerHTML;

    document.documentElement.dispatchEvent(
      new CustomEvent("bundle-picker:selected", { bubbles: true })
    );
  }
}

customElements.define("bundle-drawer", BundleDrawer);

class BundlePickerForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.productPrice = this.closest(".product-form").querySelector(".price");
    this.addButton = this.querySelector("#AddToCart");
    this.bundleProductTiles = Array.from(
      this.closest(".product-form").querySelectorAll(
        ".bundle-picker__item-action"
      )
    );

    this.addButton.addEventListener(
      "click",
      this.handleAddButtonClick.bind(this)
    );
    document.addEventListener(
      "bundle-picker:selected",
      this.handleBundlePickerSelected.bind(this)
    );
  }

  handleBundlePickerSelected() {
    const allSelected = this.bundleProductTiles.every((tile) => {
      return tile.getAttribute("data-selected") === "true";
    });
    const selectedTiles = this.bundleProductTiles.filter((tile) => {
      return tile.getAttribute("data-selected") === "true";
    });

    this.productPrice.textContent = formatMoney(
      selectedTiles.reduce((acc, tile) => {
        return acc + parseFloat(tile.getAttribute("data-price"));
      }, 0)
    );

    if (allSelected) {
      this.addButton.disabled = false;
      this.addButton.classList.remove("button--ternary");
      this.addButton.classList.add("button--primary");
      this.addButton.textContent = "In den Warenkorb";
    } else {
      this.addButton.disabled = true;
      this.addButton.classList.remove("button--primary");
      this.addButton.classList.add("button--ternary");
      this.addButton.textContent = "Wähle alle Schritte";
    }
  }

  handleAddButtonClick() {
    this.addButton.setAttribute("disabled", "disabled"),
      this.addButton.setAttribute("aria-busy", "true");
    const items = [];
    const bundleUniqueId = Math.floor(Date.now() / 1000);
    this.bundleProductTiles.forEach((tile) => {
      const poolItems = tile.querySelectorAll(
        ".bundle-picker__product-pool-item"
      );
      poolItems.forEach((poolItem) => {
        const variantId = poolItem.getAttribute("data-variant-id");
        const quantity = Number(poolItem.getAttribute("data-quantity")) || 1;
        if (variantId) {
          items.push({
            id: Number(variantId),
            quantity: quantity,
            properties: {
              "_bundleUniqueId": bundleUniqueId,
              "_bundleParentId": `gid://shopify/ProductVariant/${this.getAttribute("data-product-id")}`,
              "_bundleItemQuantity": quantity,
            }
          });
        }
      });
    });

    const reversedItems = items.reverse();

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: reversedItems,
      }),
    })
      .then((response) => response.json())
      .then(() => {
        this.addButton.removeAttribute("disabled");
        this.addButton.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true })
        );
        document.getElementById("mini-cart").open = true;
      });
  }
}

customElements.define("bundle-picker-form", BundlePickerForm);

class ProductQuantityPrice extends HTMLElement {
  connectedCallback() {
    this.productForm = this.closest(".product-form");
    this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this);
    this.quantityInput?.addEventListener("change", this.handleQuantityInputChange);
    this.bindUiEvents();
    this.syncFromQuantityInput();

    this._syncRaf = requestAnimationFrame(() => {
      this._syncRaf = null;
      this.syncFromQuantityInput();
      this.updateProductPrice({ updateAddOns: false });
    });
  }

  disconnectedCallback() {
    if (this._syncRaf) {
      cancelAnimationFrame(this._syncRaf);
      this._syncRaf = null;
    }
    this.quantityInput?.removeEventListener(
      "change",
      this.handleQuantityInputChange
    );
    this.unbindUiEvents();
  }

  bindUiEvents() {}

  unbindUiEvents() {}

  syncFromQuantityInput() {}

  updateQuantityUi() {}

  get hasQuantityBreaks() {
    return false;
  }

  get quantityInput() {
    return (
      this.querySelector('[name="quantity"]') ||
      this.productForm?.querySelector('[name="quantity"]')
    );
  }

  get variantPrice() {
    return Number(this.dataset.variantPrice) || 0;
  }

  get variantCompareAtPrice() {
    return Number(this.dataset.variantCompareAtPrice) || 0;
  }

  get selectedReduction() {
    return 0;
  }

  get selectedQuantity() {
    return Number(this.quantityInput?.value) || 1;
  }

  get reductionRounding() {
    return this.dataset.reductionRounding || "line";
  }

  handleQuantityInputChange() {
    this.syncFromQuantityInput();
    this.updateProductPrice();
  }

  getTotalSaving(unitPrice, quantity, reductionPercent) {
    if (!reductionPercent || !unitPrice || !quantity) return 0;

    const fullTotal = unitPrice * quantity;

    if (this.reductionRounding === "total") {
      const saleTotal =
        Math.floor((fullTotal * (100 - reductionPercent)) / 100 / 10) * 10;
      return fullTotal - saleTotal;
    }

    const unitSale =
      Math.floor((unitPrice * (100 - reductionPercent)) / 100 / 10) * 10;
    return (unitPrice - unitSale) * quantity;
  }

  formatMoney(cents) {
    const format = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;

    return formatMoney(cents, format);
  }

  updateProductPrice({ updateAddOns = true } = {}) {
    this.updateQuantityUi();

    const priceContainer = this.productForm?.querySelector(
      '[data-block-type="meta-price"] .price-list__price-container'
    );
    if (!priceContainer || !this.variantPrice) return;

    const quantity = this.selectedQuantity;
    const totalSaving = this.getTotalSaving(
      this.variantPrice,
      quantity,
      this.selectedReduction
    );
    const fullPrice = this.variantPrice * quantity;
    const price = Math.max(fullPrice - totalSaving, 0);
    let unitCompareAt = this.variantCompareAtPrice;

    if (totalSaving > 0 && !(unitCompareAt > this.variantPrice)) {
      unitCompareAt = this.variantPrice;
    }

    const compareAt =
      unitCompareAt > 0 && totalSaving > 0
        ? unitCompareAt * quantity
        : unitCompareAt > this.variantPrice
          ? unitCompareAt * quantity
          : 0;
    const onSale = compareAt > price;
    const unitPriceEl = priceContainer.querySelector(
      ":scope > .price.text--subdued"
    );
    const pricesWrapper =
      priceContainer.querySelector(":scope > .price-list__prices") ||
      priceContainer;

    const saleLabel = window.themeVariables?.strings?.productSalePrice || "";
    const regularLabel =
      window.themeVariables?.strings?.productRegularPrice || "";
    const perUnitTemplate =
      window.themeVariables?.strings?.productQuantityBreaksPerUnit ||
      "[[price]]";
    let unitSalePrice = this.variantPrice;
    if (totalSaving > 0 && quantity > 0) {
      if (this.reductionRounding === "total") {
        unitSalePrice = Math.floor(price / quantity);
      } else {
        unitSalePrice =
          Math.floor(
            (this.variantPrice * (100 - this.selectedReduction)) / 100 / 10
          ) * 10;
      }
    }

    const perUnitText = perUnitTemplate.replace(
      "[[price]]",
      this.formatMoney(unitSalePrice)
    );
    const perUnitHtml = `<span class="price price--per-unit hidden-pocket" data-price-per-unit>${perUnitText}</span>`;

    let pricesHtml = "";
    if (onSale) {
      pricesHtml = `
        <span class="price price--highlight price--large">
          <span class="visually-hidden">${saleLabel}</span>
          ${this.formatMoney(price)}
        </span>
        <span class="price price--compare">
          <span class="visually-hidden">${regularLabel}</span>
          ${this.formatMoney(compareAt)}
        </span>
        ${perUnitHtml}
      `;
    } else {
      pricesHtml = `
        <span class="price price--large">
          <span class="visually-hidden">${saleLabel}</span>
          ${this.formatMoney(price)}
        </span>
        ${perUnitHtml}
      `;
    }

    if (pricesWrapper.classList?.contains("price-list__prices")) {
      pricesWrapper.innerHTML = pricesHtml;
    } else {
      priceContainer.querySelectorAll(":scope > .price").forEach((el) => {
        if (el !== unitPriceEl) el.remove();
      });
      priceContainer.insertAdjacentHTML("afterbegin", pricesHtml);
    }

    this.productForm?.querySelectorAll("[data-price-per-unit]").forEach((el) => {
      if (el.textContent !== perUnitText) {
        el.textContent = perUnitText;
      }
    });

    this.updateStickyPrice(price);
    this.updateAddToCartButton(price);
    this.updateQuantityBreakSaving(totalSaving, this.selectedReduction);

    if (updateAddOns) {
      this.updateAddOnPrices();
    }
  }

  updateAddToCartButton(price) {
    const template =
      window.themeVariables?.strings?.productFormAddToCartWithQuantity ||
      "[[quantity]] · [[price]]";
    const label = template
      .replace("[[quantity]]", String(this.selectedQuantity))
      .replace("[[price]]", this.formatMoney(price));

    document
      .querySelectorAll("[data-product-add-to-cart-button]")
      .forEach((button) => {
        if (button.disabled) return;

        const textElement = button.querySelector(".loader-button__text");
        if (textElement) {
          textElement.textContent = label;
        } else {
          button.textContent = label;
        }

        button.setAttribute("data-button-content", label);
      });
  }

  updateQuantityBreakSaving(totalSaving, reductionPercent) {
    const savingEl = this.productForm?.querySelector(
      "[data-quantity-break-saving]"
    );
    if (!savingEl) return;

    if (!(totalSaving > 0) || !(reductionPercent > 0)) {
      savingEl.hidden = true;
      savingEl.textContent = "";
      return;
    }

    const template =
      window.themeVariables?.strings?.productQuantityBreaksSaveVsSingle ||
      "[[savings]] ([[percent]]%)";
    savingEl.textContent = template
      .replace("[[savings]]", this.formatMoney(totalSaving))
      .replace("[[percent]]", String(reductionPercent));
    savingEl.hidden = false;
  }

  updateAddOnPrices() {
    this.productForm?.querySelectorAll("add-on").forEach((addOn) => {
      if (typeof addOn.updateQuantityInputState === "function") {
        addOn.updateQuantityInputState();
      } else if (typeof addOn.updatePriceDisplay === "function") {
        addOn.updatePriceDisplay();
      }
    });
  }

  updateStickyPrice(price) {
    const stickyPrice = document.querySelector(".product-sticky-form__price");
    if (stickyPrice) {
      stickyPrice.textContent = this.formatMoney(price);
    }
  }
}

customElements.define("product-quantity-price", ProductQuantityPrice);

class QuantityBreaks extends ProductQuantityPrice {
  get hasQuantityBreaks() {
    return true;
  }

  get selectedRadio() {
    return this.querySelector(".quantity-breaks__radio:checked");
  }

  get selectedReduction() {
    return Number(this.selectedRadio?.dataset.reduction) || 0;
  }

  get selectedQuantity() {
    return (
      Number(this.quantityInput?.value) ||
      Number(this.selectedRadio?.dataset.quantity) ||
      1
    );
  }

  bindUiEvents() {
    this.handleChange = this.handleChange.bind(this);
    this.addEventListener("change", this.handleChange);
  }

  unbindUiEvents() {
    this.removeEventListener("change", this.handleChange);
  }

  handleChange(event) {
    const radio = event.target.closest(".quantity-breaks__radio");
    if (!radio) return;
    this.setQuantity(radio.dataset.quantity || radio.value);
  }

  updateQuantityUi() {
    this.updateSelectedUnitQuantityLabel();
    this.updateTierPrices();
  }

  updateSelectedUnitQuantityLabel() {
    const quantity = String(this.selectedQuantity);
    this.querySelectorAll("[data-quantity-breaks-selected-qty]").forEach((el) => {
      if (el.textContent !== quantity) {
        el.textContent = quantity;
      }
    });
  }

  // Selected tier uses the stepper quantity; others keep their break quantity.
  updateTierPrices() {
    const actualQuantity = this.selectedQuantity;
    const unitPrice = this.variantPrice;
    if (!unitPrice || !(actualQuantity > 0)) return;

    const savingTemplate =
      window.themeVariables?.strings?.productVolumePricingSaving ||
      "[[savings]]";

    this.querySelectorAll(".quantity-breaks__tier").forEach((tier) => {
      const radio = tier.querySelector(".quantity-breaks__radio");
      if (!radio) return;

      const breakQuantity = Number(radio.dataset.quantity) || 1;
      const reduction = Number(radio.dataset.reduction) || 0;
      const quantity = radio.checked ? actualQuantity : breakQuantity;
      const totalSaving = this.getTotalSaving(unitPrice, quantity, reduction);
      const fullPrice = unitPrice * quantity;
      const salePrice = Math.max(fullPrice - totalSaving, 0);

      const priceEl = tier.querySelector("[data-quantity-breaks-price]");
      if (priceEl) {
        priceEl.textContent = this.formatMoney(salePrice);
      }

      const compareWrap = tier.querySelector("[data-quantity-breaks-compare]");
      const compareEl = tier.querySelector("[data-quantity-breaks-compare-value]");
      if (compareWrap && compareEl) {
        if (totalSaving > 0) {
          compareEl.textContent = this.formatMoney(fullPrice);
          compareWrap.hidden = false;
        } else {
          compareWrap.hidden = true;
          compareEl.textContent = "";
        }
      }

      const saveWrap = tier.querySelector("[data-quantity-breaks-save]");
      const saveEl = tier.querySelector("[data-quantity-breaks-save-value]");
      if (saveWrap && saveEl) {
        if (totalSaving > 0) {
          saveEl.textContent = savingTemplate.replace(
            "[[savings]]",
            this.formatMoney(totalSaving)
          );
          saveWrap.hidden = false;
        } else {
          saveWrap.hidden = true;
          saveEl.textContent = "";
        }
      }
    });
  }

  syncFromQuantityInput() {
    const quantityInput = this.quantityInput;
    if (!quantityInput) return;

    const matched = this.selectMatchingRadio(quantityInput.value || 1);

    if (!matched) {
      const fallbackRadio =
        this.querySelector('.quantity-breaks__radio:checked') ||
        this.querySelector('.quantity-breaks__radio[data-quantity="1"]') ||
        this.querySelector(".quantity-breaks__radio");
      if (fallbackRadio) {
        fallbackRadio.checked = true;
        this.setQuantity(fallbackRadio.dataset.quantity || fallbackRadio.value);
      }
    }
  }

  selectMatchingRadio(quantity) {
    const selectedQuantity = Number(quantity);
    if (!Number.isFinite(selectedQuantity) || selectedQuantity <= 0) return false;

    const radios = Array.from(this.querySelectorAll(".quantity-breaks__radio"));
    if (!radios.length) return false;

    // Pick the highest break that is still <= the chosen quantity
    // e.g. breaks 1/3/6 with qty 4 or 5 → select 3
    let matchingRadio = null;
    let matchingBreakQuantity = -1;

    radios.forEach((radio) => {
      const breakQuantity = Number(radio.dataset.quantity || radio.value);
      if (
        Number.isFinite(breakQuantity) &&
        breakQuantity <= selectedQuantity &&
        breakQuantity > matchingBreakQuantity
      ) {
        matchingBreakQuantity = breakQuantity;
        matchingRadio = radio;
      }
    });

    if (!matchingRadio) return false;

    radios.forEach((radio) => {
      radio.checked = false;
    });
    matchingRadio.checked = true;
    return true;
  }

  setQuantity(quantity) {
    const quantityInput = this.quantityInput;
    if (!quantityInput || !quantity) return;

    const quantityValue = String(quantity);
    const currentValue = String(quantityInput.value || "");

    if (quantityInput.tagName === "SELECT") {
      let option = Array.from(quantityInput.options).find(
        (opt) => opt.value === quantityValue
      );

      if (!option) {
        option = document.createElement("option");
        option.value = quantityValue;
        option.textContent = quantityValue;
        quantityInput.appendChild(option);
      }

      quantityInput.value = quantityValue;
    } else if ("quantity" in quantityInput) {
      quantityInput.quantity = quantityValue;
    } else {
      quantityInput.value = quantityValue;
    }

    // Skip no-op changes so add-on/price updates don't run twice and flicker.
    if (currentValue === quantityValue) {
      this.updateProductPrice();
      return;
    }

    quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

customElements.define("quantity-breaks", QuantityBreaks);

class LineItemWarranty extends HTMLElement {
  // The line item properties the warranty app reads. The covered product and
  // the units it covers set the price, the covered title names the line, and
  // the form tells a warranty sold alongside its product from one sold alone.
  static COVERS = "_warrantyProductId";
  static UNITS = "_warrantyQuantity";
  static COVERED_TITLE = "_warrantyProductTitle";
  static FORM = "_warrantyForm";

  static syncing = false;
  static pendingCart = null;
  static holdRefresh = false;
  static openMiniCartAfterSync = false;

  // Warranties also change without anyone touching a checkbox, so the cart is
  // watched as well as the element.
  static watchCart() {
    document.addEventListener("cart:updated", (event) => {
      if (event.detail?.cart) {
        this.sync(event.detail.cart);
      }
    });

    // The theme fires cart:refresh in the same turn as cart:updated, using
    // section HTML from before _warrantyQuantity is rewritten. Holding that
    // rerender (and any that arrive while a sync is in flight) stops a stale
    // drawer paint from winning the race. openMiniCart has to be replayed on
    // the later refresh, or add-to-cart never opens the drawer.
    document.addEventListener(
      "cart:refresh",
      (event) => {
        if (this.holdRefresh) {
          if (event.detail?.openMiniCart) {
            this.openMiniCartAfterSync = true;
          }
          event.stopImmediatePropagation();
        }
      },
      true,
    );

    fetch(`${window.themeVariables.routes.cartUrl}.js`)
      .then((response) => response.json())
      .then((cart) => this.sync(cart))
      .catch((error) => {
        console.error("Failed to check warranties:", error);
      });
  }

  static diff(cart) {
    if (!window.themeVariables?.settings?.warrantyOffered) {
      const blocked = {};
      cart.items.forEach((item) => {
        if (item.properties?.[this.COVERS]) blocked[item.key] = 0;
      });

      if (Object.keys(blocked).length === 0) return null;
      return { orphans: blocked, requantified: [] };
    }

    const covered = new Map();
    cart.items.forEach((item) => {
      if (item.properties?.[this.COVERS]) return;

      const productId = String(item.product_id);
      if (!covered.has(productId)) covered.set(productId, item);
    });

    const orphans = {};
    const requantified = [];

    cart.items.forEach((item) => {
      const coveredProductId = item.properties?.[this.COVERS];
      if (!coveredProductId) return;

      const parent = covered.get(String(coveredProductId));

      if (!parent) {
        orphans[item.key] = 0;
      } else if (
        String(item.properties[this.UNITS]) !== String(parent.quantity)
      ) {
        requantified.push({ item, parent });
      }
    });

    if (Object.keys(orphans).length === 0 && requantified.length === 0) {
      return null;
    }

    return { orphans, requantified };
  }

  // A warranty whose product has left the cart is dropped, and the rest stay
  // priced for the number of units their product is actually being bought in.
  static async sync(cart) {
    if (!cart?.items) return;

    if (this.syncing) {
      this.pendingCart = cart;
      this.holdRefresh = true;
      return;
    }

    const changes = this.diff(cart);
    if (!changes) {
      if (this.holdRefresh) {
        this.holdRefresh = false;
        this.refreshCart();
      }
      return;
    }

    this.holdRefresh = true;
    this.syncing = true;

    try {
      if (Object.keys(changes.orphans).length > 0) {
        await this.post(`${window.themeVariables.routes.cartUrl}/update.js`, {
          updates: changes.orphans,
        });
      }

      for (const { item, parent } of changes.requantified) {
        await this.post(`${window.themeVariables.routes.cartChangeUrl}.js`, {
          id: item.key,
          quantity: item.quantity,
          properties: this.propertiesFor(
            parent.product_id,
            parent.quantity,
            parent.product_title,
          ),
        });
      }
    } catch (error) {
      console.error("Failed to sync warranties:", error);
    } finally {
      this.syncing = false;
      const next = this.pendingCart;
      this.pendingCart = null;

      if (next) {
        await this.sync(next);
      } else {
        this.holdRefresh = false;
        this.refreshCart();
      }
    }
  }

  static propertiesFor(productId, units, coveredTitle) {
    const properties = {
      [this.COVERS]: String(productId),
      [this.UNITS]: String(units),
      [this.FORM]: "combo",
    };

    if (coveredTitle) {
      properties[this.COVERED_TITLE] = coveredTitle;
    }

    return properties;
  }

  static async post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${url}`);
    }

    return response.json();
  }

  static refreshCart() {
    const openMiniCart = this.openMiniCartAfterSync;
    this.openMiniCartAfterSync = false;

    if (
      window.themeVariables.settings.cartType === "page" ||
      window.themeVariables.settings.pageType === "cart"
    ) {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { openMiniCart },
      }),
    );
  }

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.handleChange = this.handleChange.bind(this);

    if (this.checkbox) {
      this.checkbox.addEventListener("change", this.handleChange);
    }
  }

  disconnectedCallback() {
    if (this.checkbox) {
      this.checkbox.removeEventListener("change", this.handleChange);
    }
  }

  get productId() {
    return this.dataset.productId;
  }

  get productTitle() {
    return this.dataset.productTitle;
  }

  get units() {
    return this.dataset.units;
  }

  get variantId() {
    return this.dataset.variantId;
  }

  get warrantyKey() {
    return this.dataset.warrantyKey;
  }

  async handleChange(event) {
    event.stopPropagation();
    this.checkbox.disabled = true;
    this.setAttribute("aria-busy", "true");

    try {
      if (this.checkbox.checked) {
        await this.addWarranty();
      } else {
        await this.removeWarranty();
      }
      LineItemWarranty.refreshCart();
    } catch (error) {
      this.checkbox.checked = !this.checkbox.checked;
      console.error("Failed to update warranty:", error);
      this.checkbox.disabled = false;
      this.removeAttribute("aria-busy");
    }
  }

  async addWarranty() {
    await LineItemWarranty.post(
      `${window.themeVariables.routes.cartAddUrl}.js`,
      {
        items: [
          {
            id: Number(this.variantId),
            quantity: 1,
            properties: LineItemWarranty.propertiesFor(
              this.productId,
              this.units,
              this.productTitle,
            ),
          },
        ],
      },
    );
  }

  async removeWarranty() {
    let lineKey = this.warrantyKey;

    if (!lineKey) {
      const cart = await fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
        (response) => response.json(),
      );
      const warrantyItem = cart.items.find(
        (item) =>
          String(item.properties?.[LineItemWarranty.COVERS]) ===
          String(this.productId),
      );
      lineKey = warrantyItem?.key;
    }

    if (!lineKey) return;

    await LineItemWarranty.post(
      `${window.themeVariables.routes.cartChangeUrl}.js`,
      {
        id: lineKey,
        quantity: 0,
      },
    );
  }
}

if (!customElements.get("line-item-warranty")) {
  customElements.define("line-item-warranty", LineItemWarranty);
  LineItemWarranty.watchCart();
}

class CxOpener extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.open.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  onKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.open();
    }
  }

  open() {
    const button = document.querySelector("#cx-armin-chat-widget-v2 button");
    if (button) button.click();
  }
}

if (!customElements.get("cx-opener")) {
  customElements.define("cx-opener", CxOpener);
}
